import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { PREDICTION_FIELDS, todayISO } from "@/lib/constants";
import { Header } from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Trophy, Pencil, X, Save, Users, AlertTriangle, RotateCcw, KeyRound } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LegalEditor from "@/components/admin/LegalEditor";
import BroadcastPanel from "@/components/admin/BroadcastPanel";
import SharedNewMatchForm from "@/components/NewMatchForm";
import LiveScoreImportCard from "@/components/admin/LiveScoreImportCard";
import DatabaseCleanupCard from "@/components/admin/DatabaseCleanupCard";
import MatchDayList from "@/components/admin/MatchDayList";
import HistoricalDataSyncCard from "@/components/admin/HistoricalDataSyncCard";
import UserResetAuditCard from "@/components/admin/UserResetAuditCard";
import UsersWithoutPasswordCard from "@/components/admin/UsersWithoutPasswordCard";
import SeasonKickoffCard from "@/components/admin/SeasonKickoffCard";
import PitchDeckManagerCard from "@/components/admin/PitchDeckManagerCard";
import DemoRequestsInboxCard from "@/components/admin/DemoRequestsInboxCard";

function UserRow({ user, onDeleted, onChanged }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const isAdmin = user.role === "admin";

  const onConfirmDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${user.id}`);
      toast.success(
        `${data.deleted_user} removed${data.predictions_removed ? ` (${data.predictions_removed} predictions cleared)` : ""}`,
      );
      setConfirmOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleEditor = async () => {
    setEditorBusy(true);
    try {
      const next = !user.is_editor;
      await api.patch(`/admin/users/${user.id}/editor`, { is_editor: next });
      toast.success(next ? `${user.name || user.email} is now an editor` : `Editor access removed`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditorBusy(false);
    }
  };

  const sendResetLink = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/send-reset`);
      toast.success(`Reset link sent to ${data.sent_to} — expires in ${data.expires_in_minutes} min`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <>
      <tr
        className="border-b border-zinc-100 last:border-b-0"
        data-testid={`admin-user-${user.id}`}
      >
        <td className="px-6 py-3 font-medium text-zinc-950">{user.name || "—"}</td>
        <td className="px-6 py-3 text-zinc-700">{user.email}</td>
        <td className="px-6 py-3 text-right">
          <div className="flex items-center justify-end gap-1.5 flex-wrap">
            {user.is_bot && (
              <span
                data-testid={`admin-user-bot-badge-${user.id}`}
                className="inline-flex items-center justify-center px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20"
              >
                Bot
              </span>
            )}
            {user.is_editor && !isAdmin && (
              <span
                data-testid={`admin-user-editor-badge-${user.id}`}
                className="inline-flex items-center justify-center px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200"
              >
                Editor
              </span>
            )}
            <span
              className={`inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest ${
                isAdmin ? "bg-[#0038FF] text-white" : "bg-zinc-100 text-zinc-700"
              }`}
            >
              {user.role}
            </span>
          </div>
        </td>
        <td className="px-6 py-3 text-right">
          {isAdmin ? (
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">Protected</span>
          ) : (
            <div className="inline-flex items-center gap-3 justify-end">
              <button
                onClick={toggleEditor}
                disabled={editorBusy}
                data-testid={`admin-toggle-editor-${user.id}`}
                className={`inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] transition-colors ${
                  user.is_editor ? "text-emerald-700 hover:text-emerald-900" : "text-zinc-500 hover:text-zinc-950"
                }`}
              >
                {user.is_editor ? "Remove editor" : "Make editor"}
              </button>
              <button
                onClick={sendResetLink}
                disabled={resetBusy}
                data-testid={`admin-send-reset-${user.id}`}
                title={`Email a password-reset link to ${user.email}`}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-[#0038FF] transition-colors disabled:opacity-50"
              >
                <KeyRound className="h-3.5 w-3.5" /> {resetBusy ? "Sending…" : "Send reset"}
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                data-testid={`admin-delete-user-${user.id}`}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-[#FF3B30] transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          )}
        </td>
      </tr>

      <Dialog open={confirmOpen} onOpenChange={(v) => !busy && setConfirmOpen(v)}>
        <DialogContent
          className="bg-white rounded-sm border-zinc-200 sm:max-w-md"
          data-testid={`admin-delete-confirm-${user.id}`}
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-[#FF3B30]" />
              Delete user?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              You&apos;re about to permanently delete{" "}
              <span className="font-semibold text-zinc-950">{user.name || user.email}</span>{" "}
              <span className="text-zinc-400">({user.email})</span>. All of their predictions
              and league points will be erased. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              data-testid={`admin-delete-cancel-${user.id}`}
              className="h-11 rounded-sm border-zinc-300"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onConfirmDelete}
              disabled={busy}
              data-testid={`admin-delete-confirm-btn-${user.id}`}
              className="h-11 rounded-sm bg-[#FF3B30] hover:bg-[#E0331F] text-white font-semibold tracking-tight active:scale-[0.98]"
            >
              {busy ? "Deleting…" : "Yes, delete user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NewMatchForm({ onCreated }) {
  return <SharedNewMatchForm onCreated={onCreated} />;
}

function ResultRow({ match, onChanged }) {
  const initial = match.actual || {};
  const [vals, setVals] = useState({
    goals: initial.goals ?? "",
    shots_on_target: initial.shots_on_target ?? "",
    corners: initial.corners ?? "",
    offsides: initial.offsides ?? "",
    throw_ins: initial.throw_ins ?? "",
    fouls: initial.fouls ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Edit-match state
  const [editing, setEditing] = useState(false);
  const [editVals, setEditVals] = useState({
    home_team: match.home_team,
    away_team: match.away_team,
    kickoff: match.kickoff,
    match_date: match.match_date,
  });
  const [editSaving, setEditSaving] = useState(false);

  const setField = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const setEditField = (k, v) => setEditVals((s) => ({ ...s, [k]: v }));

  const saveResult = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const f of PREDICTION_FIELDS) {
      const n = parseInt(vals[f.key], 10);
      if (Number.isNaN(n) || n < 0) {
        toast.error(`Enter a valid number for ${f.label}`);
        return;
      }
      payload[f.key] = n;
    }
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/result`, payload);
      toast.success("Result saved & scores updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const k of ["home_team", "away_team", "kickoff", "match_date"]) {
      const v = (editVals[k] || "").toString().trim();
      if (!v) {
        toast.error(`${k.replace("_", " ")} is required`);
        return;
      }
      if (v !== match[k]) payload[k] = v;
    }
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    setEditSaving(true);
    try {
      await api.patch(`/matches/${match.id}`, payload);
      toast.success("Match updated");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditVals({
      home_team: match.home_team,
      away_team: match.away_team,
      kickoff: match.kickoff,
      match_date: match.match_date,
    });
    setEditing(false);
  };

  const remove = async () => {
    const ok = window.confirm(
      `Delete ${match.home_team} vs ${match.away_team}?\n\nThis removes the match from the admin list AND from every user's predictions page. This cannot be undone.`
    );
    if (!ok) return;
    setRemoving(true);
    try {
      await api.delete(`/matches/${match.id}`);
      toast.success("Match deleted");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemoving(false);
    }
  };

  const isFinished = match.status === "finished";

  return (
    <article className="border border-zinc-200 rounded-sm" data-testid={`admin-match-${match.id}`}>
      <header className="px-6 py-4 border-b border-zinc-200 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{match.kickoff} · {match.match_date}</span>
            {isFinished ? (
              <Badge className="rounded-sm bg-zinc-950 text-white hover:bg-zinc-950 text-[10px] tracking-widest">FINISHED</Badge>
            ) : (
              <Badge className="rounded-sm bg-[#0038FF] text-white hover:bg-[#0038FF] text-[10px] tracking-widest">SCHEDULED</Badge>
            )}
          </div>
          {!editing ? (
            <h3 className="font-heading text-xl font-bold tracking-tight text-zinc-950 mt-1">
              {match.home_team} <span className="text-zinc-300">vs</span> {match.away_team}
            </h3>
          ) : (
            <form onSubmit={saveEdit} className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`admin-edit-form-${match.id}`}>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Home team</Label>
                <Input value={editVals.home_team} onChange={(e) => setEditField("home_team", e.target.value)} data-testid={`admin-edit-home-${match.id}`} className="mt-1 h-10 rounded-sm border-zinc-300" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Away team</Label>
                <Input value={editVals.away_team} onChange={(e) => setEditField("away_team", e.target.value)} data-testid={`admin-edit-away-${match.id}`} className="mt-1 h-10 rounded-sm border-zinc-300" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Kickoff</Label>
                <Input value={editVals.kickoff} onChange={(e) => setEditField("kickoff", e.target.value)} data-testid={`admin-edit-kickoff-${match.id}`} className="mt-1 h-10 rounded-sm border-zinc-300" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Date</Label>
                <Input type="date" value={editVals.match_date} onChange={(e) => setEditField("match_date", e.target.value)} data-testid={`admin-edit-date-${match.id}`} className="mt-1 h-10 rounded-sm border-zinc-300" />
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex items-center gap-2">
                <Button
                  type="submit"
                  disabled={editSaving}
                  data-testid={`admin-edit-save-${match.id}`}
                  className="h-10 px-4 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold tracking-tight active:scale-[0.98]"
                >
                  <Save className="h-4 w-4 mr-1.5" /> {editSaving ? "Saving…" : "Save changes"}
                </Button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  data-testid={`admin-edit-cancel-${match.id}`}
                  className="inline-flex items-center gap-1 h-10 px-3 text-sm font-medium text-zinc-500 hover:text-zinc-950"
                >
                  <X className="h-4 w-4" /> Cancel
                </button>
              </div>
            </form>
          )}
        </div>
        {!editing && (
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setEditing(true)}
              data-testid={`admin-edit-${match.id}`}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              onClick={remove}
              disabled={removing}
              data-testid={`admin-delete-${match.id}`}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-[#FF3B30] transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        )}
      </header>
      <form onSubmit={saveResult} className="p-6">
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4 inline-flex items-center gap-1.5">
          <Trophy className="h-3.5 w-3.5" /> Actual full-time result
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {PREDICTION_FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{f.label}</Label>
              <Input
                type="number"
                min={0}
                value={vals[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
                data-testid={`admin-actual-${f.key}-${match.id}`}
                className="mt-2 h-12 rounded-sm border-zinc-300 text-lg font-semibold text-center"
              />
            </div>
          ))}
        </div>
        <div className="mt-5">
          <Button
            type="submit"
            disabled={saving}
            data-testid={`admin-save-result-${match.id}`}
            className="h-11 px-6 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold tracking-tight active:scale-[0.98]"
          >
            {saving ? "Saving…" : isFinished ? "Update result" : "Set result (finalize)"}
          </Button>
        </div>
      </form>
    </article>
  );
}

function DangerZone() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leaderboard/reset");
      toast.success(`Overall league reset · ${data.predictions_zeroed} predictions zeroed`);
      setConfirmOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-16" data-testid="admin-danger-zone">
      <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-[#FF3B30]" /> Danger zone
      </h2>
      <div className="mt-4 border border-[#FF3B30]/30 bg-[#FF3B30]/[0.03] rounded-sm p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Reset the overall league</h3>
          <p className="mt-1 text-sm text-zinc-600">
            Sets every prediction&apos;s points back to zero. Matches and predictions stay intact, so if you re-enter an
            actual result for a match the points will be recalculated. This cannot be undone.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          data-testid="admin-reset-league-btn"
          className="inline-flex items-center gap-1.5 h-11 px-5 rounded-sm bg-white border border-[#FF3B30] text-[#FF3B30] font-semibold tracking-tight hover:bg-[#FF3B30] hover:text-white active:scale-[0.98] transition-colors"
        >
          <RotateCcw className="h-4 w-4" /> Reset league to zero
        </button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(v) => !busy && setConfirmOpen(v)}>
        <DialogContent
          className="bg-white rounded-sm border-zinc-200 sm:max-w-md"
          data-testid="admin-reset-league-confirm"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-[#FF3B30]" />
              Reset overall league?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Every player&apos;s total points will be set to zero. Their submitted predictions are kept, but the
              leaderboard will start fresh. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              data-testid="admin-reset-league-cancel"
              className="h-11 rounded-sm border-zinc-300"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={reset}
              disabled={busy}
              data-testid="admin-reset-league-confirm-btn"
              className="h-11 rounded-sm bg-[#FF3B30] hover:bg-[#E0331F] text-white font-semibold tracking-tight active:scale-[0.98]"
            >
              {busy ? "Resetting…" : "Yes, reset to zero"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default function AdminPage() {
  const [matches, setMatches] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/matches?date=${date}`);
      setMatches(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Admin console</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950 mt-1">
            Run the day.
          </h1>
          <p className="mt-3 text-base text-zinc-500 max-w-xl">
            Add matches, then drop in actual full-time stats. Scores update for every player automatically.
          </p>
        </div>

        <NewMatchForm onCreated={load} />

        <div className="mt-8" data-testid="admin-import-fixtures-section">
          <LiveScoreImportCard />
        </div>

        <div className="mt-12 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Match list</h2>
            <p className="text-sm text-zinc-500 mt-1">Scores recompute the moment you save a result.</p>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">View date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="admin-view-date"
              className="mt-2 h-11 w-48 rounded-sm border-zinc-300"
            />
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {loading ? (
            <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
          ) : matches.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center">
              <p className="text-zinc-500 text-sm">No matches for this date.</p>
            </div>
          ) : (
            matches.map((m) => <ResultRow key={m.id} match={m} onChanged={load} />)
          )}
        </div>

        {/* Players list moved to /editor (bottom of Editor's Dashboard) */}

        <UserResetAuditCard />

        <UsersWithoutPasswordCard />

        <SeasonKickoffCard />

        <PitchDeckManagerCard />

        <DemoRequestsInboxCard />

        <div className="mt-12">
          <MatchDayList />
        </div>

        <BroadcastPanel />

        <LegalEditor />

        <div className="mt-12">
          <HistoricalDataSyncCard />
        </div>

        <div className="mt-12">
          <DatabaseCleanupCard />
        </div>

        <DangerZone />
      </main>
    </div>
  );
}
